<template>
  <section id="about" class="about">
    <div class="about-header">
      <h2>{{ $t('about.content.heading') }}</h2>
      <h3>{{ $t('about.content.subheading') }}</h3>
    </div>

    <div class="about-shell">
      <article
        v-for="(beat, index) in storyBeats"
        :key="beat.image.id"
        class="content-block"
      >
        <figure class="image-container" :class="index % 2 === 0 ? 'left' : 'right'">
          <img
            :src="beat.image.src"
            :srcset="beat.image.srcSet"
            sizes="(max-width: 768px) calc(100vw - 40px), 430px"
            alt=""
            loading="lazy"
            decoding="async"
          />
        </figure>

        <div class="text-container">
          <p v-for="(paragraph, paragraphIndex) in beat.paragraphs" :key="paragraphIndex">
            {{ paragraph }}
          </p>
        </div>
      </article>
    </div>
  </section>
</template>

<script setup>
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';
import { aboutImages as images } from '@/data/media';


const { tm } = useI18n();

const paragraphs = computed(() => {
  // Get all keys under about.content.description
  const description = tm('about.content.description');
  // If it's an object, convert values to array, otherwise return empty array
  return description ? Object.values(description) : [];
});

const storyBeats = computed(() => {
  const [openingParagraph, ...remainingParagraphs] = paragraphs.value;
  if (!openingParagraph) return [];

  const beats = [{
    image: images[0],
    paragraphs: [openingParagraph],
  }];

  for (let index = 0; index < remainingParagraphs.length; index += 2) {
    beats.push({
      image: images[Math.min(index + 2, images.length - 1)],
      paragraphs: remainingParagraphs.slice(index, index + 2),
    });
  }

  return beats;
});

</script>

<style scoped>
.about {
  padding: clamp(76px, 8vw, 112px) 20px;
  background: #fff;
}

.about-header {
  max-width: 980px;
  margin: 0 auto 54px;
  text-align: center;
}

.about-header h2 {
  margin-bottom: 18px;
}

.about-header h3 {
  max-width: 780px;
  margin: 0 auto;
  color: var(--muted-color);
  font-size: clamp(1.2rem, 2vw, 1.55rem);
  font-weight: 450;
  line-height: 1.45;
}

.about-shell {
  max-width: 1080px;
  margin: 0 auto;
}

.content-block {
  position: relative;
  display: grid;
  grid-template-columns: minmax(280px, 0.82fr) minmax(0, 1.18fr);
  gap: clamp(34px, 5vw, 64px);
  align-items: center;
  padding: 26px 0 48px;
}

.content-block:not(:last-child)::after {
  content: '';
  position: absolute;
  right: 12%;
  bottom: 0;
  left: 12%;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(38, 57, 77, 0.1), transparent);
}

.image-container {
  position: relative;
  margin: 0;
}

.image-container.right {
  order: 2;
}

.image-container::after {
  content: '\266A';
  position: absolute;
  right: 14px;
  bottom: -18px;
  z-index: 2;
  color: var(--secondary-color);
  font-size: 4.8rem;
  line-height: 1;
  opacity: 0.08;
  transform: rotate(-8deg);
}

.image-container.right::after {
  right: auto;
  left: 14px;
  transform: rotate(8deg);
}

.image-container img {
  position: relative;
  display: block;
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  border: 5px solid #fff;
  border-radius: var(--radius);
  box-shadow: var(--shadow-md);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.image-container:hover img {
  box-shadow: var(--shadow-lg);
  transform: translateY(-3px) rotate(-0.5deg);
}

.image-container.right:hover img {
  transform: translateY(-3px) rotate(0.5deg);
}

.text-container {
  color: #4d5b68;
  font-size: clamp(1rem, 1.35vw, 1.12rem);
  line-height: 1.76;
}

.text-container p {
  margin: 0;
}

.text-container p + p {
  margin-top: 1.25rem;
}

@media (max-width: 800px) {
  .content-block {
    grid-template-columns: 1fr;
    gap: 24px;
    padding-bottom: 42px;
  }

  .image-container {
    width: min(100%, 520px);
    margin: 0 auto;
    order: 0 !important;
  }

}
</style>
